import { html } from 'htm/preact'
import { useState, useEffect } from 'preact/hooks'
import { api } from '../api.js'
import { groupByCategory, money } from '../units.js'
import CategorySection from '../components/CategorySection.js'
import Sheet from '../components/Sheet.js'
import RecipeEditor from './RecipeEditor.js'

export default function Recipes({ showToast }) {
  const [recipes, setRecipes] = useState([])
  const [ingredients, setIngredients] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  useEffect(() => {
    Promise.all([
      api('/api/recipes').then(d => d.recipes || []),
      api('/api/ingredients').then(d => d.ingredients || []),
    ])
      .then(([r, i]) => { setRecipes(r); setIngredients(i) })
      .catch(e => showToast(e.message, true))
      .finally(() => setLoading(false))
  }, [])

  async function reload() {
    try {
      const [r, i] = await Promise.all([
        api('/api/recipes').then(d => d.recipes || []),
        api('/api/ingredients').then(d => d.ingredients || []),
      ])
      setRecipes(r)
      setIngredients(i)
    } catch (e) {
      showToast(e.message, true)
    }
  }

  function openNew() {
    setEditingId('new')
    setEditorOpen(true)
  }

  function openEdit(id) {
    setEditingId(id)
    setEditorOpen(true)
  }

  function closeEditor() {
    setEditorOpen(false)
  }

  function onSaved() {
    setEditorOpen(false)
    reload()
  }

  function filtered() {
    const q = search.trim().toLowerCase()
    if (!q) return recipes
    return recipes.filter(r =>
      r.name.toLowerCase().includes(q) || (r.category || '').toLowerCase().includes(q)
    )
  }

  if (loading) return html`<div class="empty">Loading…</div>`

  const filteredList = filtered()
  const groups = groupByCategory(filteredList)

  return html`
    <div class="screen-head">
      <h1>Recipes</h1>
      <p class="sub">Tap a dish to edit costs</p>
    </div>
    <input type="search" class="search" placeholder="Filter recipes…" value=${search} onInput=${e => setSearch(e.target.value)} />
    ${filteredList.length === 0
      ? html`<div class="empty">${search ? 'No recipes match your search.' : 'No recipes yet. Tap + to add one.'}</div>`
      : groups.map(([cat, items]) => html`
          <${CategorySection}
            category=${cat}
            count=${items.length}
            total=${money(items.reduce((s, r) => s + r.total_cost, 0))}
          >
            <div class="cat-cards">
              ${items.map(r => html`
                <article class="card card--press" onClick=${() => openEdit(r.id)}>
                  <h2 class="card__title">${r.name}</h2>
                  <div class="card__header-row">
                    <div class="money">${money(r.total_cost)} <span class="money-label">total</span></div>
                    <span class="card__unit">${r.sales_unit}</span>
                  </div>
                  ${r.cost_per_piece != null && html`<div class="card__meta">${money(r.cost_per_piece)} / piece</div>`}
                  ${r.cost_per_tray != null && html`<div class="card__meta">${money(r.cost_per_tray)} / tray</div>`}
                  ${r.cost_per_kg != null && html`<div class="card__meta">${money(r.cost_per_kg)} / kg</div>`}
                  ${r.cost_per_serving != null && r.serving_size_grams != null && html`<div class="card__meta">${money(r.cost_per_serving)} / serving (${r.serving_size_grams}g)</div>`}
                </article>
              `)}
            </div>
          </${CategorySection}>
        `)
    }
    <button type="button" class="fab" aria-label="New recipe" onClick=${openNew}>+</button>

    <${Sheet}
      open=${editorOpen}
      onClose=${() => setEditorOpen(false)}
      title=${editingId === 'new' ? 'New Recipe' : 'Edit Recipe'}
    >
      <${RecipeEditor}
        id=${editingId}
        onCancel=${() => setEditorOpen(false)}
        onSaved=${onSaved}
        showToast=${showToast}
        ingredients=${ingredients}
      />
    </${Sheet}>
  `
}
